import React, { useCallback } from "react";
import styled from "styled-components";
import {
  Color,
  Spacing,
  Transition,
  content,
  dominant,
  ellipsis,
  form,
  inputDisabled,
  transparentize,
} from "@glossy";
import { tr } from "@linkfluence/i18n";
import { Locale } from "@linkfluence/models/locale";
import Tooltip from "@ui-kit/tooltip/description";

const Only = styled.span`
  display: inline-block;
  vertical-align: middle;
  margin-left: 4px;
  color: ${dominant};
  font-style: italic;
  font-size: 10px;
  font-weight: normal;
  ${ellipsis()};
`;

const Checkbox = styled.label<{
  small: boolean;
  disabled: boolean;
  checked: Checked;
}>`
  display: block;
  color: ${form};
  cursor: pointer;
  height: 20px;
  line-height: 20px;
  font-size: 12px;
  ${(props) => props.disabled && inputDisabled()};
  &:not(:hover) ${Only} {
    visibility: hidden;
  }
  &:hover .square {
    stroke: ${(props) => !props.checked && content(props)};
  }
`;

const Box = styled.svg<{
  checked: Checked;
  viewBox: string;
  width: string;
  height: string;
  small: boolean;
}>`
  display: inline-block;
  vertical-align: middle;
  transform: ${(props) => (props.small ? "scale(0.75)" : undefined)};

  /* stylelint-disable no-descending-specificity */
  .square,
  .mark {
    transition: all ${Transition.Color};
  }

  .square {
    stroke-width: 1px;
    fill: ${(props) => (props.checked ? dominant(props) : "none")};
    stroke: ${(props) =>
      props.checked
        ? dominant(props)
        : transparentize(0.5, form(props, "primary"))};
  }

  .mark {
    stroke: ${Color.White};
    stroke-width: 2px;
    fill: none;
  }
  /* stylelint-enable no-descending-specificity */
`;

const Caption = styled.div`
  display: inline-block;
  vertical-align: middle;
  margin-left: ${Spacing.ExtraSmall};
  max-width: calc(100% - 24px);
`;

const Label = styled.div`
  vertical-align: middle;
  ${ellipsis()};
`;

const Input = styled.input`
  display: none;
`;

export type Checked = "half" | true | false;

interface IProps {
  locale: Locale;
  checked: Checked;
  onChange: (e: any, checked: Checked) => void;
  label?: string | React.ReactNode;
  disabled?: boolean;
  onSelectOnly?: (checked: Checked) => void;
  small?: boolean;
  showTooltip?: boolean;
  className?: string;
}

const KomonFormCheckbox = ({
  label,
  checked,
  onChange,
  disabled,
  onSelectOnly,
  small,
  showTooltip,
  locale,
  className,
}: IProps) => {
  const handleChange = useCallback(
    (e: React.FormEvent<HTMLElement>) => {
      e.preventDefault();
      e.stopPropagation();
      if (!disabled) {
        onChange(e, !checked);
      }
    },
    [onChange, checked, disabled]
  );
  return (
    <Tooltip content={label} disabled={!showTooltip}>
      <Checkbox
        {...{ disabled, checked, small }}
        onClick={handleChange}
        className={className}
      >
        <Input
          type="checkbox"
          checked={Boolean(checked)}
          disabled={disabled}
          onChange={handleChange}
        />
        <Box
          checked={checked}
          viewBox="0 0 18 18"
          width="18"
          height="18"
          small={small}
        >
          <rect
            className="square"
            x="1.5"
            y="1.5"
            width="15"
            height="15"
            rx="3"
          />
          {checked !== false && (
            <path
              className="mark"
              d={checked === "half" ? "M5 9 L9 9 L13 9" : "M4 9 L8 12 L14 6"}
            />
          )}
        </Box>
        {!!label && (
          <Caption>
            <Label>{label}</Label>
            {onSelectOnly && !disabled && (
              <Only
                onClick={(e) => {
                  handleChange(e);
                  onSelectOnly(true);
                }}
              >
                {tr(locale, "CHECKBOX::ONLY")}
              </Only>
            )}
          </Caption>
        )}
      </Checkbox>
    </Tooltip>
  );
};

export default KomonFormCheckbox;
